import type {
  PEER_CONNECTION_STATUS,
  Entity,
  SystemMessage,
  AreaSystemRequest,
  ReceivedMessage,
  LEAVE_AREA_REASONS,
  SystemParams,
  SharedApiRegistry,
  ApiType,
  AssetChange,
} from "@gotti/core";
import { System } from "@gotti/core";
import type { BaseClientConnector } from "./ClientConnectors/BaseClientConnector.js";
import type { JoinedAreaData } from "./ClientProcess.js";
import type { IndexedDBLoader } from './IndexedDbLoader.js';
import type { Client } from "./Client.js";
import type { ClientAssetsFacade } from "./assets/assetsFacade.js";

/**
 * Open interface for client-side API registration via declaration merging.
 *
 * Systems augment this interface to declare the API methods they provide.
 * TypeScript merges all augmentations into a single type automatically.
 *
 * @example
 * ```typescript
 * // In your system file:
 * declare module '@gotti/client' {
 *   interface ClientApiRegistry {
 *     onDraw: (cb: (ctx: CanvasRenderingContext2D) => void, zIndex?: number) => void;
 *   }
 * }
 *
 * export class CanvasDrawSystem extends ClientSystem<CanvasDrawApi>() {
 *   onInit() {
 *     this.addApi('onDraw', (cb, z) => { ... });
 *   }
 * }
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ClientApiRegistry {}

/**
 * Interface for client-side system capabilities.
 *
 * Two patterns:
 * 1. **No-arg generic**: `extends ClientSystem<MyApi>()` — standalone client system
 *    with typed `addApi()` constraints.
 * 2. **Shared inheritance**: `extends ClientSystem(SharedBase)` — for systems
 *    that share logic between client and server via a common base class.
 *
 * All dispatch methods (`dispatchToServer`, `dispatchToArea`, etc.) are injected
 * at runtime by `ClientProcess.decorate()`.  **Writing a method body for any dispatch
 * method will throw a runtime error.**
 *
 * @example
 * ```typescript
 * import { ClientSystem } from '@gotti/client';
 *
 * export class MyClientSystem extends ClientSystem<MyApi>() {
 *   // this.dispatchToServer() just works — no declares needed!
 *   // this.addApi() is constrained to keys of MyApi
 * }
 * ```
 */
export interface IClientSystem {
  // ── Identity flag ──────────────────────────────────────────────────────
  readonly __is_client: true;

  // ── Typed $api ─────────────────────────────────────────────────────────
  /** Full merged client + shared API registry. */
  $api: ClientApiRegistry & SharedApiRegistry;

  // ── Runtime-injected properties (set by ClientProcess.onAddedSystem) ──
  connector: BaseClientConnector;
  _$client: Client;
  indexedDb?: IndexedDBLoader;
  isNetworked: boolean;
  readonly $areaData: { [areaId: string]: any };
  /** Namespaced read-only asset accessor; injected by `ClientProcess.onAddedSystem`. Required — always injected before the system's lifecycle runs (no `!` needed). */
  $assets: ClientAssetsFacade;

  // ── Instance state ─────────────────────────────────────────────────────
  readonly requestCbs: Record<string, (responseData: any, error?: string) => void>;
  readonly states: Record<string, Record<string, any>>;

  // ── Convenience getters ────────────────────────────────────────────────
  readonly client: Client;
  readonly $client: Client;

  // ── Dispatch methods ───────────────────────────────────────────────────
  initialize(client: Client, connector: BaseClientConnector, indexedDb?: IndexedDBLoader): void;
  dispatchToServer(message: SystemMessage, reliable?: boolean): void;
  instantDispatchToServer(message: SystemMessage, reliable?: boolean): void;
  dispatchRaw(data: ArrayBuffer): void;
  dispatchToArea(areaId: string, message: SystemMessage, reliable?: boolean): void;
  requestArea(message: AreaSystemRequest): Promise<any>;
  dispatchToAllPeers(message: SystemMessage): void;
  dispatchToPeers(playerIds: string[], message: SystemMessage): void;
  dispatchToPeer(playerId: string, message: SystemMessage): void;
  dispatchToPeerRaw(playerId: string | number, data: ArrayBuffer, to?: string | number): void;
  dispatchToPeersRaw(playerIds: string[], data: ArrayBuffer, to?: string | number): void;
  dispatchToAllPeersAndServer(message: SystemMessage): void;
  instantDispatchToAllPeersAndServer(message: SystemMessage): void;
  addPeer(playerPublicId: string, options?: Record<string, any>, ackTimeout?: number, requestTimeout?: number): Promise<any>;
  removePeer(playerId: string | number): void;
  getPeerPing(playerId: string | number): number;
  getPeerStatus(playerId: string | number): { status: PEER_CONNECTION_STATUS; failures?: Array<{ ts: number; reason?: string }>; connectedAt?: number };
  isConnectedToPeer(playerId: string | number): boolean;
  handleUpdateState(areaId: string, state: any): void;
  readonly connectedPeers: (string | number)[];

  // ── Lifecycle hooks (optional — implement the ones you need) ───────────
  onUpdatedAreaData?(areaId: string, data: any): void;
  onUpdatedAreaState?(areaId: string, newState: any, oldState: any): void;
  onUpdatedGameData?(data: any): void;
  onUpdatedGameState?(data: any): void;
  onJoinedArea?(area: JoinedAreaData, joinedOptions: any): void;
  onLeftArea?(areaId: string, reason: LEAVE_AREA_REASONS, options?: any): void;
  onPeerSystemConnectionRequest?(playerId: string | number, opts: any): Promise<any>;
  onPeerSystemConnectionEnded?(playerId: string | number, opts?: any): void;
  onPeerSystemConnectionStarted?(playerId: string | number, opts: any): void;
  onPeerSystemMessage?(playerId: string | number, message: ReceivedMessage): void;
  onAnyPeerConnectionEnded?(playerId: string | number, opts?: any): void;
  onAnyPeerConnectionStarted?(playerId: string | number, opts?: any): void;
  onAnyPeerConnectionMissedPing?(playerId: string | number, missedPings: number): void;
  onRemoteClientEntityCreated?(entity: Entity, attributes: { id: number; data: any }): void;
  onAreaRequest?(areaId: string | number, request: ReceivedMessage): Promise<any>;
  onServerMessage?(message: ReceivedMessage): void;
  onAreaSystemStateUpdate?(areaId: string, newState: any, previousState?: any): void;
  onAreaSystemStateRemoved?(areaId: string, lastState: any): void;
  onRawAreaMessage?(data: ArrayBuffer): void;
  onRawPeerMessage?(playerId: string | number, data: ArrayBuffer, to?: string | number): void;
  /** Fired when an asset visible to this process is created or overwritten. */
  onAssetUpdated?(change: AssetChange): void;
  /** Fired when an asset visible to this process is deleted. */
  onAssetRemoved?(change: AssetChange): void;
}

/**
 * Type guard: checks whether a system has been decorated with client capabilities.
 */
export function isClientSystem(system: unknown): system is IClientSystem {
  return typeof system === 'object' && system !== null && '__is_client' in system;
}

/**
 * Type-level helper that produces a class whose instances are typed as
 * `System & IClientSystem`.
 *
 * **Zero runtime cost** — returns the base class (or `System`) unchanged.
 * All dispatch methods are injected at runtime by `ClientProcess.decorate()`.
 *
 * ### Usage patterns
 *
 * **No-arg (recommended)** — pass generics for typed `addApi()`:
 * ```typescript
 * export class MySystem extends ClientSystem<MyApi>() { }
 * ```
 *
 * **With base class** — when sharing a base between client & server:
 * ```typescript
 * export class MySystem extends ClientSystem(SharedBase) { }
 * ```
 *
 * **With base class + explicit API type** — when a subclass registers APIs
 * beyond what the base declares, pass a phantom `api<>()` second argument:
 * ```typescript
 * import { api } from '@gotti/client';
 * type FullApi = BaseApi & { extraMethod: () => void };
 * export class MySystem extends ClientSystem(SharedBase, api<FullApi>()) { }
 * ```
 */
export function ClientSystem<TProvides, TBase extends abstract new (...args: any[]) => System>(
  Base: TBase, _apiType: ApiType<TProvides>,
): new (...args: any[]) => InstanceType<TBase> & System<TProvides> & IClientSystem;
export function ClientSystem<TBase extends abstract new (...args: any[]) => System>(
  Base: TBase,
): new (...args: any[]) => InstanceType<TBase> & IClientSystem;
export function ClientSystem<TProvides = {}, TComponent = {}>():
  (new (name?: string | number | SystemParams) => System<TProvides, TComponent> & IClientSystem);
export function ClientSystem(Base?: any, _apiType?: any) {
  return (Base ?? System) as any;
}
